/**
 * - Description -
 * APILoaderDecoratorService contains all the methods that allows the webapp to make the app responsive with respect with
 * the user actions and the backend service.
 *
 * - Usage -
 * Using APILoaderDecoratorService
 *
 * import APILoaderDecoratorService from '@/helpers/APILoaderDecoratorService.js';
 * APILoaderDecoratorService.execute(requestUrl, requestBody)
 *    .then ( response => { this.yourDataSuccess = response } );
 *    .catch ( error => { this.yourDataError = error } )
 */
import APIService from "@/helpers/APIService";
import NotificationService from "@/helpers/NotificationService";
import TextService from "@/helpers/TextService";
import AlertService from "@/helpers/AlertService";
import LoaderService from "@/helpers/LoaderService";

export default {
    name: "APILoaderDecoratorService",
    setLoader: setLoader,
    setLoaderMultiple: setLoaderMultiple
};

// Progressive Number for identify functions called by the UI.
let progressiveLoaderNum = 0;


/**
 * Generic upload service handler.
 * @param requestUrl the url to be called
 * @param data the payload data, divided into interactionData and additionalElement
 * @param file the file to be uploaded
 * @returns {*} the response of the backend
 */
function setLoader(requestObject, messages, options) {
    let id = progressiveLoaderNum.toString(10);
    progressiveLoaderNum = progressiveLoaderNum + 1;
    options.group = options.group || TextService.generateRandomString(16);

    // Let's start Loader
    if (options.loadingType === "fullscreen") {
        LoaderService.activateFullscreenLoader(messages.loading);
    } else if (options.loadingType === "notification") {
        let notificationOptions = NotificationService.createInfoNotificationOptions(messages, options);
        NotificationService.addNotification(id, notificationOptions);
    } else if (options.loadingType === "fullscreen+notification") {
        LoaderService.activateFullscreenLoader(messages.loading);
        let notificationOptions = NotificationService.createInfoNotificationOptions(messages, options);
        NotificationService.addNotification(id, notificationOptions);
    } else {
        // silent - no loaders / alerts / notification
    }

    return APIService.call(requestObject.type, requestObject.urlsInfo, requestObject.data)
        // If the loader does not end in a the timeout time, then close it and notify the user
        .then(function (response) {
            if (options.loadingType === "fullscreen") {
                LoaderService.deactivateFullscreenLoader();
            } else if (options.loadingType === "notification") {
                NotificationService.dismissNotification(id);
                let notificationOptions = NotificationService.createSuccessNotificationOptions(messages, options);
                NotificationService.addNotification(id + "_post", notificationOptions);
            } else if (options.loadingType === "fullscreen+notification") {
                LoaderService.deactivateFullscreenLoader();
                NotificationService.dismissNotification(id);
                let notificationOptions = NotificationService.createSuccessNotificationOptions(messages, options);
                NotificationService.addNotification(id + "_post", notificationOptions);
            }
            else if (options.loadingType === "none") {
                // silent - no loaders / alerts / notification
            }
            return response;
        })
        .catch(function (error) {
            //console.log('APILoaderDecoratorService - ERROR: ' + requestObject.urlsInfo.url + ': ', error);
            if (options.loadingType === "fullscreen") {
                LoaderService.deactivateFullscreenLoader();
                AlertService.createAlertException(messages.error);
            } else if (options.loadingType === "notification") {
                NotificationService.dismissNotification(id);
                let notificationOptions = NotificationService.createErrorNotificationOptions(messages, options);
                NotificationService.addNotification(id + "_post", notificationOptions);
            } else if (options.loadingType === "notification") {
                // silent - no loaders / alerts / notification
            }else if (options.loadingType === "fullscreen+notification") {
                LoaderService.deactivateFullscreenLoader();
                AlertService.createAlertException(messages.error);
                NotificationService.dismissNotification(id);
                let notificationOptions = NotificationService.createErrorNotificationOptions(messages, options);
                NotificationService.addNotification(id + "_post", notificationOptions);
            }
            return error;
        });
}


/**
 * Generic upload service handler.
 * @param requestUrl the url to be called
 * @param data the payload data, divided into interactionData and additionalElement
 * @param file the file to be uploaded
 * @returns {*} the response of the backend
 */
function setLoaderMultiple(requestObjects, messages, options) {
    let steps = Object.keys(requestObjects);
    return doStep(0, steps, requestObjects, messages, options);
}

function doStep(stepNum, steps, requestObjects, messages, options) {
    let step = steps[stepNum];
    let apiCalls = getApiCalls(step, requestObjects, messages, options);
    return Promise.all(apiCalls.map(p => p.catch(() => undefined)))
        .then(function (success) {
            stepNum++;
            if (stepNum < steps.length) {
                return doStep(stepNum, steps, requestObjects, messages, options);
            }
        });
}

function getApiCalls(step, requestObjects, messages, options) {
    let apiCalls = [];
    for (let i = 0; i < requestObjects[step].length; i++) {
        apiCalls.push(setLoader(requestObjects[step][i], messages[step][i], options[step][i]));
    }
    return apiCalls;
}

